import { useComprehensiveTopNav, usePagination, useWindowResize } from 'hooks';
import { routes } from 'lib/api';
import { Button, DropdownButton, JobsTable, LoadingIndicator, Pagination, SearchBar, Typography } from 'lib/components';
import { DropdownItem } from 'lib/components/types';
import { JobStatus as JS, JobStatusType as S } from 'lib/util';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
	actions,
	RequestManager,
	useAppDispatch,
	useSelectAuthorization,
	useSelectHospitals,
	useSelectJobs,
	useSelectRequest,
} from 'store';
import { compareJobs } from 'lib/components/DataTables/Jobs/sort';
import { SortField } from 'lib/components/DataTables/Jobs/types';
import { Job } from 'types';
import classes from './index.module.scss';

/**
 * Type definition for filter items used in job status filtering
 */
type FilterItem<T = any> = { label: string; value: T };

/**
 * Quick filter buttons for common job status filters
 * These appear as toggle buttons at the top of the jobs list
 */
const JOB_STATUS_FILTERS: FilterItem<S>[] = [
	{ label: 'Pending', value: S.PENDING },
	{ label: 'Approved', value: S.ACCEPTED },
	{ label: 'Rejected', value: S.NURSE_REJECTED },
];

/**
 * Complete list of all possible job statuses for the dropdown filter
 * Used in the status dropdown filter component
 */
const JOB_STATUS_DROPDOWN_ITEMS: DropdownItem<number>[] = [
	{ value: S.PENDING, label: JS.text(S.PENDING) },
	{ value: S.ACCEPTED, label: JS.text(S.ACCEPTED) },
	{ value: S.ACKNOWLEDGED, label: JS.text(S.ACKNOWLEDGED) },
	{ value: S.ARRIVED, label: JS.text(S.ARRIVED) },
	{ value: S.NURSE_ACCEPTED, label: JS.text(S.NURSE_ACCEPTED) },
	{ value: S.NURSE_REJECTED, label: JS.text(S.NURSE_REJECTED) },
	{ value: S.TREATMENT_INITIATED, label: JS.text(S.TREATMENT_INITIATED) },
	{ value: S.TREATMENT_COMPLETED, label: JS.text(S.TREATMENT_COMPLETED) },
	{ value: S.COMPLETED, label: JS.text(S.COMPLETED) },
	{ value: S.ABORTED, label: JS.text(S.ABORTED) },
	{ value: S.CANCELLED, label: JS.text(S.CANCELLED) },
	{ value: S.VOIDED, label: JS.text(S.VOIDED) },
];

/**
 * Jobs screen component that displays a list of all jobs with filtering capabilities
 * Includes search, status filtering, and hospital filtering
 */
const JobsScreen: React.FC = () => {
	const { push } = useHistory();
	const { search } = useLocation();
	// Extract filter parameter from URL query string (e.g., ?filter=active)
	const filter = new URLSearchParams(search).get('filter');
	const { isSmallerDevice } = useWindowResize();

	const dispatch = useAppDispatch();
	// Track active API requests
	const { active } = useSelectRequest();
	// Permission check for job creation capability
	const { canCreateJob } = useSelectAuthorization();
	// Jobs and hospitals data from Redux store
	const jobs = useSelectJobs();
	const hospitals = useSelectHospitals();

	// State for hospital dropdown filter
	const [hospitalDropdownItems, setHospitalDropdownItems] = useState<DropdownItem<number>[]>([]);
	const [selectedHospitalDropdownItem, setSelectedHospitalDropdownItem] = useState<number>(-1);
	// State for job status dropdown filter
	const [selectedStatusDropdownItem, setSelectedStatusDropdownItem] = useState<number>(-1);
	const [sortField, setSortField] = useState<SortField>({ field: 'jobId', sort: 'desc' });

	// Track data loading state
	const [fetchedCount, setFetchedCount] = useState(0);
	// Search query state
	const [query, setQuery] = useState('');
	// Status filter toggle buttons state
	// Initialize based on URL filter parameter (e.g., ?filter=pending)
	const [currentFilters, setCurrentFilters] = useState<Record<S, boolean>>(
		JOB_STATUS_FILTERS.reduce((acc, cur) => {
			if (filter) {
				if (filter === 'pending' && cur.label === 'Pending') {
					acc[cur.value] = true;
				}
			} else {
				acc[cur.value] = false;
			}
			return acc;
		}, {} as Record<S, boolean>)
	);

	/**
	 * Fetch jobs and hospitals data on component mount
	 */
	useEffect(() => {
		dispatch(actions.fetchJobs());
		dispatch(actions.fetchHospitals());
	}, []);

	/**
	 * Monitor API requests to track when hospital data has finished loading
	 * Populates the hospital dropdown items when data is available
	 */
	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;

		if (RM.req(routes.HOSPITAL, 'GET')) {
			if (RM.succeeded()) {
				// Transform hospital data into dropdown items format
				setHospitalDropdownItems(() => hospitals.list.map(({ id, name }) => ({ label: name, value: id })));
				setFetchedCount((prev) => prev + 1);
			}
		}
	}, [active]);

	// Use custom pagination hook to handle job list pagination
	const { isDataFetched, listing, setData, paginatedData, paginationProps } = usePagination(jobs, {
		sortKey: sortField.field && sortField.sort ? `${sortField.field}:${sortField.sort}` : undefined,
		compare: (a, b) => compareJobs(a, b, sortField.field, sortField.sort),
	});

	/**
	 * Handle search functionality using custom hook
	 * Searches across hospital name, nurse name, and creator name
	 */
	useEffect(() => {
		const handle = window.setTimeout(() => {
			const needle = query.trim().toLowerCase();
			if (!needle) {
				setData(listing);
				return;
			}
			setData(
				listing.filter((job) => {
					const patient = (job.patientName || '').toLowerCase();
					const patientId = (job.patientId || '').toLowerCase();
					const physician = (job.orderingPhysician || '').toLowerCase();
					return (
						job.hospital.name.toLowerCase().includes(needle) ||
						(job.nurse && job.nurse.user.name.toLowerCase().includes(needle)) ||
						job.creator.name.toLowerCase().includes(needle) ||
						patient.includes(needle) ||
						patientId.includes(needle) ||
						physician.includes(needle)
					);
				})
			);
		}, 300);
		return () => window.clearTimeout(handle);
	}, [query, listing]);

	/**
	 * Handle 'active' filter from URL parameter
	 * Filters jobs to show only those with active status
	 */
	useEffect(() => {
		if (filter !== 'active') return;
		const activeJobs = listing.filter((job) => JS.isActive(job.status));
		setData(activeJobs);
	}, [filter, listing]);

	/**
	 * Apply status filters when listing changes
	 * This ensures filters are reapplied when new data is loaded
	 */
	useEffect(() => {
		const filters = Object.entries(currentFilters).filter((item) => item[1] !== false);
		if (filters.length === 0) {
			return;
		}

		const filtered: Job[] = [];
		for (const job of listing) {
			if (currentFilters[job.status]) {
				filtered.push(job);
			}
		}
		setData(filtered);
	}, [listing]);

	/**
	 * Apply status filters when filter selection changes
	 * If no filters are selected, show all jobs
	 */
	useEffect(() => {
		const filters = Object.entries(currentFilters).filter((item) => item[1] !== false);
		if (filters.length === 0) {
			setData(listing);
			return;
		}

		const filtered: Job[] = [];
		for (const job of listing) {
			if (currentFilters[job.status]) {
				filtered.push(job);
			}
		}

		setData(filtered);
	}, [currentFilters]);

	/**
	 * Toggle filter button state when clicked
	 * Memoized with useCallback to prevent unnecessary re-renders
	 */
	const handleFilterClick = useCallback(
		(filterItem: FilterItem<S>) => {
			setCurrentFilters((filters) => {
				let currentFilterValue = filters[filterItem.value];
				return {
					...filters,
					[filterItem.value]: !currentFilterValue,
				};
			});
		},
		[setCurrentFilters]
	);

	/**
	 * Filter jobs by selected hospital
	 * Only applies when a hospital is selected from the dropdown
	 */
	useEffect(() => {
		if (selectedHospitalDropdownItem === -1) return;

		const filtered = listing.filter((job) => job.hospital.id === selectedHospitalDropdownItem);
		setData(filtered);
	}, [selectedHospitalDropdownItem]);

	/**
	 * Filter jobs by selected status
	 * Only applies when a status is selected from the dropdown
	 */
	useEffect(() => {
		if (selectedStatusDropdownItem === -1) return;

		const result = listing.filter((job) => job.status === selectedStatusDropdownItem);
		setData(result);
	}, [selectedStatusDropdownItem]);

	/**
	 * Handler for hospital dropdown selection
	 */
	const handleHospitalDropdownItemSelect = (value: number) => {
		setSelectedHospitalDropdownItem(value);
	};

	/**
	 * Handler for status dropdown selection
	 */
	const handleStatusDropdownItemSelect = (value: number) => {
		setSelectedStatusDropdownItem(value);
	};

	useComprehensiveTopNav('Jobs');

	if (!isDataFetched || fetchedCount < 1) return <LoadingIndicator />;

	return (
		<div className={classes['dispatches-container']}>
			<div className={classes['row']}>
				<div className={classes['search-container']}>
					<SearchBar
						query={query}
						setQuery={setQuery}
						onSearch={() => undefined}
						className={classes['search-input-container']}
						placeholder="Patient, ID, or physician"
					/>
				</div>
				{JOB_STATUS_FILTERS.map((filterItem, idx, arr) => (
					<Button
						onClick={() => handleFilterClick(filterItem)}
						key={filterItem.value}
						title={filterItem.label}
						className={`${classes['button']}`}
						color="dark"
						style={{
							marginTop: '1rem',
							marginRight: idx === arr.length - 1 ? '1rem' : 'initial',
						}}
						isActivePrimary={currentFilters && currentFilters[filterItem.value]}
					/>
				))}
				{canCreateJob && (
					<button
						className={classes['row']}
						style={{
							marginTop: '1rem',
						}}
						onClick={() => push('/jobs/create')}
					>
						<Typography weight="medium">Add New Job</Typography>
						<div className={classes['plus-icon']}>+</div>
					</button>
				)}
			</div>
			<div
				className={classes['row']}
				style={{
					alignItems: 'center',
				}}
			>
				<Typography
					variant={isSmallerDevice ? 'h5' : 'h6'}
					weight="medium"
					style={{
						marginTop: '1rem',
					}}
				>
					Filter by:
				</Typography>
				<div
					style={{
						margin: '1rem 0.5rem 0 1rem',
					}}
				>
					<DropdownButton
						title="Status"
						list={JOB_STATUS_DROPDOWN_ITEMS}
						onItemSelect={handleStatusDropdownItemSelect}
					/>
				</div>
				<div
					style={{
						margin: '1rem 1rem 0 0',
					}}
				>
					<DropdownButton
						title="Hospital"
						list={hospitalDropdownItems}
						onItemSelect={handleHospitalDropdownItemSelect}
					/>
				</div>
			</div>
			<JobsTable jobs={paginatedData} sortField={sortField} onSortFieldChange={setSortField} />
			<Pagination {...paginationProps} />
		</div>
	);
};

export default JobsScreen;
